import { Purpose } from '@axo/shared/data-access/types';
import { useTranslation } from '@axo/shared/i18n';
import { ComponentProps, useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAxoFormContext } from '../../../../../context/AxoForm/AxoForm';
import {
  DEFAULT_LOAN_AMOUNT,
  DEFAULT_LOAN_DURATION,
  MAX_LOAN_AMOUNT,
  MIN_LOAN_AMOUNT,
  RATES,
} from '../../config/globals';
import { calculateDisclaimer } from '../../Disclaimer';
import { DenmarkFormData } from '../../FormData.dk.types';
import { getDebtsWithRefinanceAmount } from '../../utils/getDebtsWithRefinanceAmount';
import { getPurpose } from '../../utils/getPurpose';
import { getRefinanceableDebts } from '../../utils/getRefinanceableDebts';
import { getTotalRefinanceAmount } from '../../utils/getTotalRefinanceAmount';

import { ExtendedLoanOverview } from '../../../../../components/ExtendedLoanOverview/ExtendedLoanOverview';
import styles from './LoanOverviewGroup.module.scss';

enum OtherPurpose {
  Personal = 'Forbrug',
}

const refinanceableDebtsExtraProps: ComponentProps<
  typeof ExtendedLoanOverview
>['refinanceableDebtsExtraProps'] = {
  shouldUseStoredRefinanceableDebts: false,
  isTotalAmountVisible: false,
  editType: 'toggle',
};

const cashBlockProps: ComponentProps<
  typeof ExtendedLoanOverview
>['cashBlockProps'] = {
  step: 5000,
  isPermanentEdit: true,
};

export const LoanOverviewGroup = () => {
  const { t } = useTranslation();
  const isInitialPurposeDone = useRef(false);

  const { setValue, register } = useFormContext();

  const { axoFormData, onChange } = useAxoFormContext<DenmarkFormData>();

  const appliedAmount =
    axoFormData['LoanApplication/AppliedAmount'] ?? DEFAULT_LOAN_AMOUNT;
  const loanDuration =
    axoFormData['LoanApplication/LoanDuration'] ?? DEFAULT_LOAN_DURATION;

  const otherPurpose = axoFormData['LoanApplication/OtherPurpose'];

  const isRefinancing =
    axoFormData['LoanApplication/InitialPurpose'] === Purpose.RefinancingLoan;

  const labels = t('LoanOverview', { returnObjects: true }) as ComponentProps<
    typeof ExtendedLoanOverview
  >['labels'];

  const refinanceableDebts = useMemo(
    () =>
      getRefinanceableDebts({
        debts: axoFormData['LoanApplication/Debts'],
        translate: t,
      }),
    [t, axoFormData]
  );

  useEffect(() => {
    if (isInitialPurposeDone.current) return;

    isInitialPurposeDone.current = true;

    if (
      refinanceableDebts?.length &&
      axoFormData['LoanApplication/Purpose'] !== Purpose.RefinancingLoan
    ) {
      onChange({ 'LoanApplication/Purpose': Purpose.RefinancingLoan });
      setValue('LoanApplication/Purpose', Purpose.RefinancingLoan, {
        shouldTouch: true,
        shouldDirty: true,
      });
      return;
    }

    if (
      !refinanceableDebts?.length &&
      axoFormData['LoanApplication/Purpose'] === Purpose.RefinancingLoan
    ) {
      onChange({ 'LoanApplication/Purpose': Purpose.Other });
      setValue('LoanApplication/Purpose', Purpose.Other, {
        shouldTouch: true,
        shouldDirty: true,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRefinanceAmountChange: ComponentProps<
    typeof ExtendedLoanOverview
  >['onRefinanceAmountChange'] = useCallback(
    ({
      refinanceAmount: newValue,
      refinanceableDebts: _refinanceableDebts,
      totalAmount: _totalAmount,
    }) => {
      if (newValue < 0) return;

      const newTotalRefinancingAmount = getTotalRefinanceAmount({
        refinanceableDebts: _refinanceableDebts,
      });

      const debtsWithRefinanceAmountValue = getDebtsWithRefinanceAmount({
        debts: axoFormData['LoanApplication/Debts'],
        refinanceableDebts: _refinanceableDebts,
      });

      const newOtherPurpose =
        isRefinancing && newTotalRefinancingAmount === 0
          ? OtherPurpose.Personal
          : otherPurpose;

      const purpose = getPurpose({
        initialPurpose: axoFormData['Frontend/DropdownPurpose'],
        currentPurpose: axoFormData['LoanApplication/Purpose'] as Purpose,
        totalRefinanceAmount: newTotalRefinancingAmount,
      });

      onChange({
        'LoanApplication/Debts': debtsWithRefinanceAmountValue,
        'LoanApplication/TotalRefinancingAmount': newTotalRefinancingAmount,
        'LoanApplication/Purpose': purpose,
        'LoanApplication/AppliedAmount': _totalAmount,
        'LoanApplication/OtherPurpose': newOtherPurpose,
      });
      // Sometimes ??? onChange does not trigger

      const setValueOptions = {
        shouldTouch: true,
        shouldDirty: true,
      };

      setValue(
        'LoanApplication/Debts',
        debtsWithRefinanceAmountValue,
        setValueOptions
      );
      setValue(
        'LoanApplication/TotalRefinancingAmount',
        newTotalRefinancingAmount,
        setValueOptions
      );
      setValue('LoanApplication/Purpose', purpose, setValueOptions);
      setValue('LoanApplication/AppliedAmount', _totalAmount, setValueOptions);
      setValue(
        'LoanApplication/OtherPurpose',
        newOtherPurpose,
        setValueOptions
      );
    },
    [axoFormData, isRefinancing, otherPurpose, onChange, setValue]
  );

  const onTotalAmountChange = (newValue: number) => {
    if (axoFormData['LoanApplication/AppliedAmount'] === newValue) return;
    if (newValue > MAX_LOAN_AMOUNT) return;

    onChange({ 'LoanApplication/AppliedAmount': newValue });
    setValue('LoanApplication/AppliedAmount', newValue, {
      shouldTouch: true,
      shouldDirty: true,
    });
  };

  const onLoanDurationChange = (newValue: number) => {
    if (newValue < 0) return;
    if (axoFormData['LoanApplication/LoanDuration'] === newValue) return;
    onChange({ 'LoanApplication/LoanDuration': newValue });
    setValue('LoanApplication/LoanDuration', newValue, {
      shouldTouch: true,
      shouldDirty: true,
    });
  };

  const sortedRefinanceableDebts = useMemo(
    () => refinanceableDebts?.sort((a, b) => b.value - a.value) || [],
    [refinanceableDebts]
  );

  const allocationPriority = useMemo(
    () => [...sortedRefinanceableDebts.map((debt) => debt.key), 'cash'],
    [sortedRefinanceableDebts]
  );

  if (labels?.monthlyPaymentDisclaimer) {
    const tArgs = calculateDisclaimer({
      appliedAmount,
      loanDuration,
      rates: RATES,
    });

    labels['monthlyPaymentDisclaimer'] = t('Disclaimer', tArgs);
  }

  const loanApplicationID =
    sessionStorage.getItem('applicationId') ?? undefined;

  if (!refinanceableDebts) return <>Failed to load debts</>;

  return (
    <>
      <input
        type="hidden"
        {...register('LoanApplication/TotalRefinancingAmount', {
          valueAsNumber: true,
        })}
      />

      <ExtendedLoanOverview
        className={styles.loanOverviewGroup}
        loanApplicationID={loanApplicationID}
        labels={labels}
        appliedAmount={appliedAmount}
        loanDuration={loanDuration}
        minLoanAmount={MIN_LOAN_AMOUNT}
        maxLoanAmount={MAX_LOAN_AMOUNT}
        currency="kr"
        isUpSell={!isRefinancing}
        refinanceableDebts={refinanceableDebts}
        onRefinanceAmountChange={onRefinanceAmountChange}
        onTotalAmountChange={onTotalAmountChange}
        onLoanDurationChange={onLoanDurationChange}
        isEditButtonsVisible
        initialEditField="cashAmount"
        refinanceableDebtsExtraProps={refinanceableDebtsExtraProps}
        allocationPriority={allocationPriority}
        cashBlockProps={cashBlockProps}
        isLoanDurationVisible
        isDebtInfoButtonVisible={false}
        // debounced change on page load doesn't trigger onRefinanceAmountChange
        debounceRefinanceAmountChange={false}
      />
    </>
  );
};
